import { baseApi, defaultQueryFreshness } from "./baseApi";
import type { ListingItem } from "../../api/listings";

interface ApiPayload extends Record<string, unknown> {
  message?: string;
}

export const listingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchListings: builder.query<ListingItem[], void>({
      query: () => ({ url: "/listings", method: "GET", usePublic: true }),
      providesTags: [{ type: "Listings", id: "LIST" }],
      ...defaultQueryFreshness,
    }),
    fetchListingById: builder.query<ListingItem, string>({
      query: (id) => ({ url: `/listings/${id}`, method: "GET", usePublic: true }),
      providesTags: (_res, _err, id) => [{ type: "Listings", id: String(id) }],
      ...defaultQueryFreshness,
    }),
    fetchMyListings: builder.query<ListingItem[], void>({
      query: () => ({ url: "/listings/mine", method: "GET" }),
      providesTags: [{ type: "Listings", id: "MINE" }],
      ...defaultQueryFreshness,
    }),
    createListing: builder.mutation<unknown, ApiPayload>({
      query: (payload) => ({ url: "/listings", method: "POST", data: payload }),
      invalidatesTags: [{ type: "Listings", id: "LIST" }, { type: "Listings", id: "MINE" }],
    }),
    deleteListingById: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/listings/${id}`, method: "DELETE" }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Listings", id: String(id) },
        { type: "Listings", id: "LIST" },
        { type: "Listings", id: "MINE" },
      ],
    }),
    updateListingById: builder.mutation<unknown, { id: string; payload: ApiPayload }>({
      query: ({ id, payload }) => ({ url: `/listings/${id}`, method: "PATCH", data: payload }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Listings", id: String(id) },
        { type: "Listings", id: "LIST" },
        { type: "Listings", id: "MINE" },
      ],
    }),
    updateListingLocation: builder.mutation<unknown, { id: string; location: Record<string, unknown> }>({
      query: ({ id, location }) => ({ url: `/listings/${id}/location`, method: "PATCH", data: location }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Listings", id: String(id) },
        { type: "Listings", id: "LIST" },
        { type: "Listings", id: "MINE" },
      ],
    }),
  }),
});

export const {
  useFetchListingsQuery,
  useFetchListingByIdQuery,
  useFetchMyListingsQuery,
  useCreateListingMutation,
  useDeleteListingByIdMutation,
  useUpdateListingByIdMutation,
  useUpdateListingLocationMutation,
} = listingsApi;
